import React, { useState, useEffect, useRef } from 'react'
import { ChevronLeft, ChevronRight, Play, BookOpen, Home as HomeIcon, Wrench, GraduationCap, HandHeart, Users } from 'lucide-react'
import './OurWork.css'

// Placeholder images using SVG data URIs (colored placeholders)
const makePlaceholder = (color, label) => `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="800" height="500"><rect width="800" height="500" fill="${color}"/><text x="400" y="240" text-anchor="middle" font-size="22" fill="rgba(255,255,255,0.9)" font-family="sans-serif" font-weight="bold">${label}</text><text x="400" y="275" text-anchor="middle" font-size="14" fill="rgba(255,255,255,0.6)" font-family="sans-serif">Replace with actual photo</text></svg>`)}`

const workSections = [
  {
    id: 'education',
    icon: BookOpen,
    title: 'Free Education Program',
    desc: 'We run daily classes for children from Class 1 through Class 10, completely free of charge. Trained teachers from the community deliver structured curricula aligned to state board standards. All books, stationery, school bags, and uniforms are provided by the Samiti.',
    media: [
      { type: 'image', src: '/media/image8.jpg', caption: 'Morning assembly — Children gather before classes' },
      { type: 'image', src: '/media/image10.jpg', caption: 'Interactive night classroom sessions with trained local teachers at Sarkubandha, Raleguda GP, Chitrakonda, Malkangiri, Odisha' },
      { type: 'image', src: '/media/image11.jpg', caption: 'Night School Annual Assesment — 99% pass rate achieved in 2025 with personal guidence by Guruji and Guruma at Raleguda.' },
    ],
    stats: ['200+ enrolled', '99% pass rate', 'Class 1–12'],
  },
  {
    id: 'hostel',
    icon: HomeIcon,
    title: 'Hostel Facility',
    desc: 'Many of our children come from villages 10–15 km away. Our hostel provides safe, clean dormitories, nutritious meals three times a day, and a supervised study environment. The facility runs completely free for all BPL enrolled students.',
    media: [
      { type: 'image', src: makePlaceholder('#0f766e', 'Hostel Dormitory'), caption: 'Clean and well-lit dormitories for 120 resident children' },
      { type: 'image', src: makePlaceholder('#0d9488', 'Dining Hall'), caption: 'Nutritious meals served three times a day' },
      { type: 'image', src: makePlaceholder('#14b8a6', 'Study Hour'), caption: 'Supervised evening study hours in the common room' },
    ],
    stats: ['120 beds', '3 meals/day', '12 villages served'],
  },
  {
    id: 'skills',
    icon: Wrench,
    title: 'Skill Development & Vocational Training',
    desc: 'Our skill centre runs free courses in tailoring and stitching, basic computing, mobile phone repair, and agricultural techniques for children aged 14 and above. Graduates receive a certificate of completion and are assisted with job placement.',
    media: [
      { type: 'image', src: makePlaceholder('#7c3aed', 'Tailoring Workshop'), caption: 'Tailoring workshop — 80+ graduates placed in employment' },
      { type: 'image', src: makePlaceholder('#6d28d9', 'Computer Lab'), caption: 'Computer lab with 20 workstations donated by supporters' },
      { type: 'video', poster: makePlaceholder('#5b21b6', 'Skill Demo Video'), videoUrl: '', caption: 'Watch: skill development graduation ceremony 2023' },
    ],
    stats: ['4 courses', '80+ placed', '14+ age group'],
  },
  {
    id: 'tuition',
    icon: GraduationCap,
    title: 'Free Tuition Classes',
    desc: 'After regular school hours, our tuition centre offers remedial classes in Mathematics, Science, and English — subjects where tribal children often need extra support. Our volunteer tutors have helped reduce dropout rates significantly.',
    media: [
      { type: 'image', src: makePlaceholder('#b45309', 'Evening Tuition'), caption: 'Evening tuition for government school students' },
      { type: 'image', src: makePlaceholder('#d97706', 'Math Class'), caption: 'Mathematics made engaging with activity-based learning' },
    ],
    stats: ['150 students', 'Dropout rate ↓60%', 'Volunteer tutors'],
  },
  {
    id: 'welfare',
    icon: HandHeart,
    title: 'Child Welfare & Health',
    desc: 'Monthly health check-ups, vaccination drives in partnership with the district health department, and provision of hygiene kits to all resident children. Our welfare officers ensure children receive emotional support and counselling.',
    media: [
      { type: 'image', src: makePlaceholder('#be185d', 'Health Camp'), caption: 'Monthly health check-up camp conducted by partnering doctors' },
      { type: 'image', src: makePlaceholder('#db2777', 'Vaccination Drive'), caption: 'Annual vaccination drive in collaboration with district health' },
    ],
    stats: ['Monthly check-ups', 'Hygiene kits', 'Mental wellness'],
  },
  {
    id: 'community',
    icon: Users,
    title: 'Community Awareness',
    desc: 'We run bi-annual awareness campaigns in tribal villages about child rights, girl child education, and government welfare schemes. We help families access BPL certificates and connect them with government programs they are entitled to.',
    media: [
      { type: 'image', src: '/media/image0.jpeg', caption: 'Village meeting on child education rights — 300 parents attended' },
      { type: 'image', src: makePlaceholder('#2563eb', 'Awareness Camp'), caption: 'Awareness session on girl child education in tribal hamlets' },
    ],
    stats: ['12 villages', '300+ parents', 'Annual campaigns'],
  },
]

function Slideshow({ items }) {
  const [cur, setCur] = useState(0)
  const [playing, setPlaying] = useState(false)
  const intervalRef = useRef(null)

  const go = (dir) => {
    setCur(c => (c + dir + items.length) % items.length)
    setPlaying(false)
  }

  useEffect(() => { return () => clearInterval(intervalRef.current) }, [])

  const item = items[cur]

  return (
    <div className="slideshow">
      <div className="slideshow__viewport">
        {item.type === 'image' ? (
          <img src={item.src} alt={item.caption} className="slideshow__img" loading="lazy"/>
        ) : (
          <div className="slideshow__video-wrap">
            <img src={item.poster} alt={item.caption} className="slideshow__img"/>
            {!playing && (
              <button className="slideshow__play-btn" onClick={() => setPlaying(true)}>
                <Play size={28} fill="white"/>
              </button>
            )}
          </div>
        )}
        {items.length > 1 && (
          <>
            <button className="slideshow__nav slideshow__nav--prev" onClick={() => go(-1)}><ChevronLeft size={22}/></button>
            <button className="slideshow__nav slideshow__nav--next" onClick={() => go(1)}><ChevronRight size={22}/></button>
          </>
        )}
        <div className="slideshow__caption">{item.caption}</div>
        {items.length > 1 && (
          <div className="slideshow__dots">
            {items.map((_, i) => (
              <button key={i} className={`slideshow__dot${i === cur ? ' active' : ''}`} onClick={() => setCur(i)}/>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function Reveal({ children }) {
  const ref = useRef(null)
  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) { ref.current.classList.add('visible'); o.disconnect() } }, { threshold: 0.1 })
    if (ref.current) o.observe(ref.current)
    return () => o.disconnect()
  }, [])
  return <div ref={ref} className="reveal-el">{children}</div>
}

export default function OurWork() {
  return (
    <div className="ourwork-page">
      <section className="ourwork-hero">
        <div className="container ourwork-hero__content">
          <span className="section-tag">Programs & Impact</span>
          <h1 className="section-title">Our Work on the Ground</h1>
          <p className="section-desc" style={{margin:'0 auto'}}>
            Six active programs running free of cost for tribal BPL children — education, shelter, skills, and more.
          </p>
          <div className="ourwork-hero__tabs">
            {workSections.map(s => (
              <a key={s.id} href={`#${s.id}`} className="ourwork-tab">
                <s.icon size={16}/>
                <span>{s.title.split(' ')[0]} {s.title.split(' ')[1]}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {workSections.map((section, idx) => (
        <section key={section.id} id={section.id} className={`work-section page-section ${idx % 2 === 1 ? 'work-section--alt' : ''}`}>
          <div className="container">
            <Reveal>
              <div className={`work-section__inner ${idx % 2 === 1 ? 'work-section__inner--rev' : ''}`}>
                <div className="work-section__media">
                  <Slideshow items={section.media}/>
                </div>
                <div className="work-section__text">
                  <div className="work-section__icon-wrap">
                    <section.icon size={24}/>
                  </div>
                  <h2 className="work-section__title">{section.title}</h2>
                  <p className="work-section__desc">{section.desc}</p>
                  <div className="work-section__stats">
                    {section.stats.map(stat => (
                      <div key={stat} className="work-stat">
                        <CheckmarkIcon/> {stat}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      ))}
    </div>
  )
}

function CheckmarkIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{flexShrink:0}}>
      <circle cx="8" cy="8" r="8" fill="#0ea5e9" fillOpacity="0.15"/>
      <path d="M5 8l2 2 4-4" stroke="#0ea5e9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}
